/**
 * Shared redirect utility hook
 *
 * Provides a consistent redirect function with toast notification
 * and 3-second delay across all pages.
 */

import { useCallback, useState } from "react";

import { toastMessageActions } from "../state/request";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

export interface RedirectWithToastResult {
  /** Whether a redirect has been already triggered */
  hasRedirected: boolean;
  /** Function to trigger redirect with toast notification */
  redirectWithToast: (
    message: string,
    destination: string,
    severity?: "error" | "warning" | "info"
  ) => void;
}

/**
 * Hook for redirecting with toast notifications
 *
 * @return {RedirectWithToastResult} Redirect utilities
 *
 * @example
 * ```typescript
 * const { redirectWithToast, hasRedirected } = useRedirectWithToast();
 *
 * if (error) {
 *   redirectWithToast(
 *     "Something went wrong",
 *     "/dashboard",
 *     "error"
 *   );
 * }
 * ```
 */
export const useRedirectWithToast = (): RedirectWithToastResult => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [hasRedirected, setHasRedirected] = useState(false);

  const redirectWithToast = useCallback(
    (
      message: string,
      destination: string,
      severity: "error" | "warning" | "info" = "error",
    ) => {
      // Prevent multiple redirects
      if (hasRedirected) return;

      setHasRedirected(true);

      dispatch(
        toastMessageActions.setGlobalAlert({
          severity,
          message,
        }),
      );

      // Delay redirect for 3 seconds so user can see the toast
      setTimeout(() => {
        navigate(destination);
      }, 3000);
    },
    [hasRedirected, dispatch, navigate],
  );

  return {
    hasRedirected,
    redirectWithToast,
  };
};

